import { FC, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useI18n } from "../../hooks/useTranslations";
import { useDueDatesApi } from "../../http/dueDates/api";
import { RenewDueDates } from "../../http/dueDates/types";
import { getErrorRenewDueDate } from "../../types/utils";
import Button from "../ui/Button";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import ModalDialog from "../ui/ModalDialog";

type RenewDueDatesDialogProps = {
  dueDates: {
    id: string;
    carId: string;
    date: string;
  }[];
  onClose: (reset: boolean) => void;
};

export interface RenewFormBody {
  date: string;
}

const RenewDueDatesDialog: FC<RenewDueDatesDialogProps> = ({ dueDates, onClose }: RenewDueDatesDialogProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dueDatesApi = useDueDatesApi();

  const onSubmit = async (formData: RenewFormBody): Promise<void> => {
    const dueDateIds: string[] = [];
    const carIds: string[] = [];
    let errDate = false;

    dueDates.forEach((dueDate) => {
      const date = new Date(dueDate.date);
      const formDate = new Date(formData.date);
      if (date > formDate) {
        dispatch(showErrorNotification(i18n, "", getErrorRenewDueDate(formData.date, dueDate.date, i18n)));
        errDate = true;
      } else {
        dueDateIds.push(dueDate.id);
        carIds.push(dueDate.carId);
      }
    });

    const body: RenewDueDates = {
      date: formData.date,
      dueDateIds: dueDateIds,
    };

    if (errDate == false) {
      setIsSubmitting(true);
      try {
        await dueDatesApi.renewDueDates(body, carIds);
        dispatch(showSuccessNotification(i18n.t));
        onClose(true);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={() => onClose(false)} title={i18n.t.dueDates.renewPlural}>
      <Form<RenewFormBody>
        onSubmit={() => onSubmit}
        submitText={i18n.t.common.save}
        disabled={isSubmitting}
        secondaryButton={
          <Button
            type="secondary"
            buttonProps={{
              onClick: async () => onClose(false),
            }}
          >
            {i18n.t.common.cancel}
          </Button>
        }
      >
        {({ control, watch, setValue }) => {
          return (
            <FormFieldsContainer>
              <Controller
                name="date"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => {
                    const d = new Date(value!);
                    if (d.getFullYear() < 1000) {
                      return i18n.t.common.invalidDate;
                    }
                    return undefined;
                  },
                }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.common.date} error={fieldState.error}>
                    {({ isInvalid }) => (
                      <DatePicker
                        onChange={(v) => !!v && setValue("date", v)}
                        value={watch("date")}
                        isInvalid={isInvalid}
                      />
                    )}
                  </FormField>
                )}
              />
            </FormFieldsContainer>
          );
        }}
      </Form>
    </ModalDialog>
  );
};

export default RenewDueDatesDialog;
